
import { Injectable } from '@angular/core';
import { AutocompleteControl } from '@root/app/shared/components/dynamic-form/controls/autocomplete';
import { DatepickerControl } from '@root/app/shared/components/dynamic-form/controls/datepicker';
import { DropdownControl } from '@root/app/shared/components/dynamic-form/controls/dropdown';
import { ImageBoxControl } from '@root/app/shared/components/dynamic-form/controls/imagebox';
import { DynamicControl } from '@root/app/shared/models/dynamic-form-control.model';
import { Rule } from '@shared/models/rule.model';
import { isNullOrUndefined } from '@shared/utils/utils';
import { TableControl } from '@root/app/shared/components/dynamic-form/controls/table';
import * as moment from 'moment/moment';



@Injectable({
    providedIn: 'root'
})
export class MetadataService {


  getFirstOccurrence(ocrFullTextAnnotation, value){
    return this.getOccurrences(ocrFullTextAnnotation, value)[0];
  }

  getOccurrences(ocrFullTextAnnotation, value) {
    if (isNullOrUndefined(ocrFullTextAnnotation) || ocrFullTextAnnotation.length <= 0) {
      return [];
    }

    const occurrences = [];
    ocrFullTextAnnotation.forEach((page) => {
      const wordPage = new Array();
      page.fullTextAnnotation.pages.forEach((strings) => {
        strings.blocks.forEach((block) => {
          block.paragraphs.forEach((paragraph) => {
            // in case value to be recognised exists out of multiple words
            this.addIfMatches(value, paragraph.text, page.page, paragraph.bounding_box, occurrences);
            paragraph.words.forEach((word) => {
              if (word.text !== paragraph.text) {
                this.addIfMatches(value, word.text, page.page, word.bounding_box, occurrences);
              }
            });
          });
        });
      });
    });

    occurrences.sort((a, b) => a.page - b.page);
    return occurrences;
  }

  addIfMatches(value, text, page, boundingBox, occurrences) {
    const wordMatches = text.localeCompare(value, undefined, { sensitivity: 'accent' }) === 0;
    const dateMatches =  this.getDate(text).diff(this.getDate(value), 'days') === 0;

    if (wordMatches || dateMatches) {
      const wordWithBoundingBox = { page: parseInt(page, 10), vertices: boundingBox.vertices };
      occurrences.push(wordWithBoundingBox);
    }
  }

  getDate(value: any) {
    const allowedDateFormats = ['DD/MM/YYYY', 'DD/MM/YY'];
    return moment(value, allowedDateFormats);
  }


  getMetadataFormObj(formControls: any, values: any): DynamicControl<any>[] {
        let formControlsRet: DynamicControl<any>[] = [];
        formControlsRet = formControls.map((elem: any) => {
            const rules = this.getRules(elem);
            switch (elem.type.toLowerCase()) {
                case 'text':
                case 'number': {
                    let value: any;
                    if ((elem.value != null && elem.value && Array.isArray(elem.value) && elem.value.length > 1) || (elem.value === null)) {
                        value = '';
                    } else {
                        value = this.getFieldValue(!isNullOrUndefined(values) ? values[elem.name] : null, elem.name, elem.type)[0];
                    }
                    return new AutocompleteControl({
                        key: elem.name,
                        label: elem.description,
                        value,
                        valueOptions: '',
                        type: elem.type,
                        required: this.isVisible(elem.viewState) ? rules.required : null,
                        viewState: elem.viewState,
                        rangeValues: this.isVisible(elem.viewState) ? rules.range : null,
                        regex: this.isVisible(elem.viewState) ? rules.regex : null,
                        MOD97: this.isVisible(elem.viewState) ? rules.MOD97 : null,
                    });
                }
                case 'enum': {
                    return new DropdownControl({
                        key: elem.name,
                        label: elem.description,
                        value: (values === null) ? '' : this.getFieldValue(values[elem.name], elem.name, elem.type),
                        required: this.isVisible(elem.viewState) ? rules.required : null,
                        viewState: elem.viewState,
                        rangeValues: this.isVisible(elem.viewState) ? rules.range : null,
                        regex: this.isVisible(elem.viewState) ? rules.regex : null,
                        MOD97: this.isVisible(elem.viewState) ? rules.MOD97 : null,
                        options: elem.enum.map((option: any) => {
                            return {
                                key: option,
                                value: option
                            };
                        }
                        )
                    });
                }
                case 'date': {
                    return new DatepickerControl({
                        key: elem.name,
                        label: elem.description,
                        value: (values === null) ? '' : this.getFieldValue(values[elem.name], elem.name, elem.type),//values[elem.name] ? moment(values[elem.name]).format() : null,
                        type: elem.type,
                        required: this.isVisible(elem.viewState) ? rules.required : null,
                        viewState: elem.viewState,
                        rangeValues: this.isVisible(elem.viewState) ? rules.range : null,
                        regex: this.isVisible(elem.viewState) ? rules.regex : null
                    });
                }

                case 'image': {
                    return new ImageBoxControl({
                        key: elem.name,
                        label: elem.description,
                        value: (values === null) ? '' : this.getFieldValue(values[elem.name], elem.name, elem.type),
                        type: elem.type,
                        equired: this.isVisible(elem.viewState) ? rules.required : null,
                        viewState: elem.viewState
                    })
                }

                case 'table': {
                    return new TableControl({
                        key: elem.name,
                        label: elem.description,
                        value: elem.value,
                        type: elem.type,
                        colSize: elem.colSize,
                        disabled: elem.disabled,
                        annotate: elem.annotate,
                        color: elem.boundingboxColor,
                        viewState: elem.viewState,
                        colSizes: [],
                        columns: this.filterInvisibleColumns(elem.columns),
                        displayBoundingBox: elem.displayBoundingBox,
                        boundingBoxSet: isNullOrUndefined(elem.boundingboxes) ? false : true,
                        children: (values && values[elem.name]) ? this.getChildren(elem, values[elem.name]) : []
                    })
                }

                default: {
                    return {};
                }
            }
        });
        return formControlsRet;
    }


    getRules(elem: any) {
        const ruleObject = new Rule({});
        if (elem.rules != null) {
            const regexArray = new Array();
            elem.rules.map((rule: any) => {
                switch (rule.type) {
                    case 'Required': {
                        ruleObject.setRequired(true);
                        break;
                    }

                    case 'Range': {
                        ruleObject.setRange(rule.min, rule.max);
                        break;
                    }

                    case 'Regex': {
                        regexArray.push(rule.regex)
                        break;
                    }

                    case 'MOD97': {
                        ruleObject.setMOD97(true);
                        break;
                    }

                }
                if (ruleObject.required === null) {
                    ruleObject.setRequired(false);
                }
                if (ruleObject.MOD97 === null) {
                    ruleObject.setMOD97(false);
                }
            });

            ruleObject.setRegex(regexArray);
        }
        return ruleObject;
    }

    getFieldValue(fieldValues: any, name: any, type: any) {
        const values = new Array();
        if (!isNullOrUndefined(fieldValues)) {
            if (Array.isArray(fieldValues)) {
                for (const field of fieldValues) {
                    values.push(type === 'number' ? (field.value.replace(",", ".")) : field.value);
                }
            } else {
                values.push(type === 'number' ? (fieldValues.value.replace(",", ".")) : fieldValues.value);
            }

        }
        return values;
    }

    getChildren(elem: any, values: any){
      let rows;
      if(Array.isArray(values)){
        rows = values[0].value;
      }
      else {
        rows = values.value;
      }
        let returnChildren = [];
        rows.forEach(row => {
            returnChildren.push(this.getMetadataFormObj(elem.columns, row));
        });
        return returnChildren;
    }

    filterInvisibleColumns(columns: any){
      return columns.filter(column => this.isVisible(column.viewState))
    }

    isVisible(viewState){
        return viewState !== "Invisible" && viewState !== "NotSorrento"
    }
}
